import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Loader2, CalendarIcon } from 'lucide-react';
import { TAX_CATEGORY_OPTIONS } from '@/shared/lib/tax-category-options';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import {
  useOrderForEdit,
  useCreateOrder,
  useUpdateOrder,
  useCustomerOrganisations,
  useSalesUsers,
  useUsers,
  useSubscriptions as useContracts,
  useSites,
} from '../../hooks';
import type {
  Quote,
  QuoteStatus,
  CreateQuoteInput,
  UpdateQuoteInput,
} from '../../types';
import {
  isPopulatedOrganisation,
  isPopulatedUser,
  isPopulatedContract,
  isPopulatedSite,
} from '../../types';

// =============================================================================
// Status options for orders
// =============================================================================

const STATUS_OPTIONS: { value: QuoteStatus; labelKey: string }[] = [
  { value: 'approved', labelKey: 'status.approved' },
  { value: 'confirmed', labelKey: 'status.confirmed' },
  { value: 'inprogress', labelKey: 'status.inprogress' },
  { value: 'invoice-ready', labelKey: 'status.invoice-ready' },
  { value: 'partially-invoiced', labelKey: 'status.partially-invoiced' },
  { value: 'invoiced', labelKey: 'status.invoiced' },
];

const CURRENCY_OPTIONS = [
  { value: 'CAD', label: 'CAD' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const PAYMENT_TERMS_OPTIONS = [
  { value: 'on-order', label: 'On Order' },
  { value: 'net-0', label: 'Net 0' },
  { value: 'net-30', label: 'Net 30' },
  { value: 'net-45', label: 'Net 45' },
  { value: 'net-60', label: 'Net 60' },
];

const NONE_VALUE = '__none__';

// =============================================================================
// Modal
// =============================================================================

interface OrderFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quote row for edit (provides mongoQuoteId) */
  editOrder?: Quote | null;
}

export function OrderFormModal({
  open,
  onOpenChange,
  editOrder,
}: OrderFormModalProps) {
  const { t } = useTranslation('orders');
  const isEdit = !!editOrder;
  const mongoQuoteId = editOrder?.mongoQuoteId || null;

  // Fetch V3 data when editing
  const editQuery = useOrderForEdit(mongoQuoteId);
  const v3Data = editQuery.data;

  // Mutations
  const createMutation = useCreateOrder();
  const updateMutation = useUpdateOrder();

  // Form dropdowns
  const customersQuery = useCustomerOrganisations();
  const salesUsersQuery = useSalesUsers();

  // Form state
  const [status, setStatus] = useState<QuoteStatus>('approved');
  const [customerId, setCustomerId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [salesManagerId, setSalesManagerId] = useState('');
  const [contractualManagerId, setContractualManagerId] = useState('');
  const [contractId, setContractId] = useState('');
  const [description, setDescription] = useState('');
  const [comment, setComment] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [language, setLanguage] = useState('en');
  const [taxCategory, setTaxCategory] = useState('none');
  const [paymentTerms, setPaymentTerms] = useState('net-30');
  const [expirationDate, setExpirationDate] = useState<Date | undefined>();
  const [estimatedDelivery, setEstimatedDelivery] = useState<Date | undefined>();
  const [acceptedDate, setAcceptedDate] = useState<Date | undefined>();
  const [poNumber, setPoNumber] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [billingSiteId, setBillingSiteId] = useState('');
  const [shippingSiteId, setShippingSiteId] = useState('');
  const [hubspotLink, setHubspotLink] = useState('');
  const [gdriveLink, setGdriveLink] = useState('');
  const [nextStep, setNextStep] = useState('');

  // Dependent dropdowns
  const orgUsersQuery = useUsers(customerId || undefined);
  const contractsQuery = useContracts(customerId || undefined);
  const sitesQuery = useSites(customerId || undefined);

  // Populate form when V3 data loads
  useEffect(() => {
    if (!v3Data) return;

    setStatus(v3Data.status);
    setProjectName(v3Data.project_name || '');
    setDescription(v3Data.description || '');
    setComment(v3Data.comment || '');
    setInternalNotes(v3Data.internal_notes || '');
    setCurrency(v3Data.currency || 'CAD');
    setLanguage(v3Data.language || 'en');
    setTaxCategory(v3Data.tax_category || 'none');
    setPaymentTerms(v3Data.payment_terms || 'net-30');
    setPoNumber(v3Data.po_number || '');
    setBillingAddress(v3Data.billing_address || '');
    setShippingAddress(v3Data.shipping_addresses?.[0]?.address || '');
    setHubspotLink(v3Data.hubspot_link || '');
    setGdriveLink(v3Data.gdrive_link || '');
    setNextStep(v3Data.next_step || '');

    if (v3Data.expiration_date) setExpirationDate(parseISO(v3Data.expiration_date));
    if (v3Data.estimated_delivery) setEstimatedDelivery(parseISO(v3Data.estimated_delivery));
    if (v3Data.accepted_at) setAcceptedDate(parseISO(v3Data.accepted_at));

    // Populated references
    if (isPopulatedOrganisation(v3Data.customer)) {
      setCustomerId(v3Data.customer._id);
    } else if (typeof v3Data.customer === 'string') {
      setCustomerId(v3Data.customer);
    }
    if (isPopulatedUser(v3Data.sales_manager)) {
      setSalesManagerId(v3Data.sales_manager._id);
    } else if (typeof v3Data.sales_manager === 'string') {
      setSalesManagerId(v3Data.sales_manager);
    }
    if (isPopulatedUser(v3Data.contractual_manager)) {
      setContractualManagerId(v3Data.contractual_manager._id);
    } else if (typeof v3Data.contractual_manager === 'string') {
      setContractualManagerId(v3Data.contractual_manager);
    }
    if (isPopulatedContract(v3Data.contract)) {
      // Resolve MongoDB ObjectId to PG UUID via contracts dropdown data
      const mongoId = v3Data.contract._id;
      const pgMatch = contracts.find(c => c.mongoContractId === mongoId);
      setContractId(pgMatch?._id || mongoId);
    } else if (typeof v3Data.contract === 'string') {
      const pgMatch = contracts.find(c => c.mongoContractId === v3Data.contract);
      setContractId(pgMatch?._id || v3Data.contract);
    }
    if (isPopulatedSite(v3Data.billing_site)) {
      setBillingSiteId(v3Data.billing_site._id);
    } else if (typeof v3Data.billing_site === 'string') {
      setBillingSiteId(v3Data.billing_site);
    }
    const shippingSite = v3Data.shipping_addresses?.[0]?.site;
    if (isPopulatedSite(shippingSite)) {
      setShippingSiteId(shippingSite._id);
    } else if (typeof shippingSite === 'string') {
      setShippingSiteId(shippingSite);
    }
  }, [v3Data]);

  // Reset form when closing
  useEffect(() => {
    if (!open) {
      setStatus('approved');
      setCustomerId('');
      setProjectName('');
      setSalesManagerId('');
      setContractualManagerId('');
      setContractId('');
      setDescription('');
      setComment('');
      setInternalNotes('');
      setCurrency('CAD');
      setLanguage('en');
      setTaxCategory('none');
      setPaymentTerms('net-30');
      setExpirationDate(undefined);
      setEstimatedDelivery(undefined);
      setAcceptedDate(undefined);
      setPoNumber('');
      setBillingAddress('');
      setShippingAddress('');
      setBillingSiteId('');
      setShippingSiteId('');
      setHubspotLink('');
      setGdriveLink('');
      setNextStep('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!customerId || !projectName || !salesManagerId) return;

    const formatDateStr = (d: Date | undefined) =>
      d ? format(d, 'yyyy-MM-dd') : undefined;

    // Resolve PG UUID → MongoDB ObjectId for the contract field (quotes live in MongoDB)
    const resolvedContractId = contractId
      ? (contracts.find(c => c._id === contractId)?.mongoContractId || undefined)
      : undefined;

    if (isEdit && mongoQuoteId) {
      const data: UpdateQuoteInput = {
        status,
        customer: customerId,
        project_name: projectName,
        sales_manager: salesManagerId,
        contractual_manager: contractualManagerId || undefined,
        contract: resolvedContractId,
        description: description || undefined,
        comment: comment || undefined,
        internal_notes: internalNotes || undefined,
        currency: currency as any,
        language: language as any,
        tax_category: taxCategory as any,
        payment_terms: paymentTerms as any,
        expiration_date: formatDateStr(expirationDate),
        estimated_delivery: formatDateStr(estimatedDelivery),
        accepted_at: formatDateStr(acceptedDate),
        po_number: poNumber || undefined,
        billing_site: billingSiteId || undefined,
        billing_address: billingAddress || undefined,
        shipping_addresses: (shippingSiteId || shippingAddress)
          ? [{ site: shippingSiteId || undefined, address: shippingAddress || '' }]
          : [],
        hubspot_link: hubspotLink || undefined,
        gdrive_link: gdriveLink || undefined,
        next_step: nextStep || undefined,
      };

      // Auto-set accepted_at when transitioning to approved
      if (status === 'approved' && !acceptedDate && v3Data?.status !== 'approved') {
        data.accepted_at = format(new Date(), 'yyyy-MM-dd');
      }

      await updateMutation.mutateAsync({ id: mongoQuoteId, data });
      onOpenChange(false);
    } else {
      const data: CreateQuoteInput = {
        customer: customerId,
        project_name: projectName,
        sales_manager: salesManagerId,
        status: 'approved',
        currency: currency as any,
        language: language as any,
        tax_category: taxCategory as any,
        payment_terms: paymentTerms as any,
        date: format(new Date(), 'yyyy-MM-dd'),
        expiration_date: formatDateStr(expirationDate),
        description: description || undefined,
      };

      await createMutation.mutateAsync(data);
      onOpenChange(false);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isLoading = isEdit && editQuery.isLoading;

  const customers = customersQuery.data || [];
  const salesUsers = salesUsersQuery.data || [];
  const orgUsers = orgUsersQuery.data || [];
  const contracts = contractsQuery.data || [];
  const sites = sitesQuery.data || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('form.editTitle') : t('form.createTitle')}
            {isEdit && editOrder?.quoteNumber && ` #${editOrder.quoteNumber}`}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('form.editDescription') : t('form.createDescription')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">{t('tabs.details')}</TabsTrigger>
              <TabsTrigger value="shipping">{t('tabs.shipping')}</TabsTrigger>
              <TabsTrigger value="notes">{t('tabs.notes')}</TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 mt-4">
              {/* Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('fields.status')}</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as QuoteStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.poNumber')}</Label>
                  <Input
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    placeholder={t('fields.poNumber')}
                  />
                </div>
              </div>

              {/* Customer */}
              <div className="space-y-2">
                <Label>{t('fields.customer')} *</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Project Name */}
              <div className="space-y-2">
                <Label>{t('fields.projectName')} *</Label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder={t('fields.projectName')}
                />
              </div>

              {/* Managers */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('fields.salesManager')} *</Label>
                  <Select value={salesManagerId} onValueChange={setSalesManagerId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('fields.selectSalesManager')} />
                    </SelectTrigger>
                    <SelectContent>
                      {salesUsers.map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {[u.firstname, u.lastname].filter(Boolean).join(' ') || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.contractualManager')}</Label>
                  <Select value={contractualManagerId} onValueChange={setContractualManagerId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('fields.selectContractualManager')} />
                    </SelectTrigger>
                    <SelectContent>
                      {orgUsers.map((u) => (
                        <SelectItem key={u._id} value={u._id}>
                          {[u.firstname, u.lastname].filter(Boolean).join(' ') || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contract */}
              {customerId && (
                <div className="space-y-2">
                  <Label>{t('fields.contract')}</Label>
                  <Select value={contractId || NONE_VALUE} onValueChange={(v) => setContractId(v === NONE_VALUE ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('fields.selectContract')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>—</SelectItem>
                      {contracts.map((c) => (
                        <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Financial */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('fields.currency')}</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.taxCategory')}</Label>
                  <Select value={taxCategory} onValueChange={setTaxCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TAX_CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.paymentTerms')}</Label>
                  <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('fields.acceptedDate')}</Label>
                  <DatePicker date={acceptedDate} onDateChange={setAcceptedDate} placeholder={t('fields.pickDate')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.estimatedDelivery')}</Label>
                  <DatePicker date={estimatedDelivery} onDateChange={setEstimatedDelivery} placeholder={t('fields.pickDate')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.expirationDate')}</Label>
                  <DatePicker date={expirationDate} onDateChange={setExpirationDate} placeholder={t('fields.pickDate')} />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>{t('fields.description')}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Links */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('fields.hubspotLink')}</Label>
                  <Input
                    value={hubspotLink}
                    onChange={(e) => setHubspotLink(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('fields.gdriveLink')}</Label>
                  <Input
                    value={gdriveLink}
                    onChange={(e) => setGdriveLink(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </TabsContent>

            {/* Shipping Tab */}
            <TabsContent value="shipping" className="space-y-4 mt-4">
              {/* Billing */}
              <div className="space-y-2">
                <Label>{t('fields.billingSite')}</Label>
                <Select value={billingSiteId || NONE_VALUE} onValueChange={(v) => setBillingSiteId(v === NONE_VALUE ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.selectBillingSite')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>—</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('fields.billingAddress')}</Label>
                <Textarea
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Shipping */}
              <div className="space-y-2">
                <Label>{t('fields.shippingSite')}</Label>
                <Select value={shippingSiteId || NONE_VALUE} onValueChange={(v) => setShippingSiteId(v === NONE_VALUE ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.selectShippingSite')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>—</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('fields.shippingAddress')}</Label>
                <Textarea
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Next Step */}
              <div className="space-y-2">
                <Label>{t('fields.nextStep')}</Label>
                <Input
                  value={nextStep}
                  onChange={(e) => setNextStep(e.target.value)}
                />
              </div>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('fields.comment')}</Label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('fields.internalNotes')}</Label>
                <Textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('form.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !customerId || !projectName || !salesManagerId}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('form.saving')}
              </>
            ) : (
              t('form.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// DatePicker helper
// =============================================================================

function DatePicker({
  date,
  onDateChange,
  placeholder,
}: {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  placeholder: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground',
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, 'yyyy-MM-dd') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onDateChange}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
